import { useState, useEffect } from 'react';
import {
  Stack,
  FormControl,
  FormLabel,
  HStack
} from '@chakra-ui/react';
import SearchBar from '../../SearchBar/SearchBar';
import StatusDropdown from '../../Dropdown/StatusDropdown/StatusDropdown';
import SuggestionTypeDropdown from '../../Dropdown/SuggestionTypeDropdown/SuggestionTypeDropdown';
import ArtistDropdown from '../../Artist/ArtistDropdown/ArtistDropdown';
import { ProcessStatus } from 'models/types';
import { SuggestionType } from 'models/ListingDataSuggestion/types';
import { ArtistModel } from 'models';

export interface ListingDataSuggestionsFiltersFormProps {
  defaultStatus?: ProcessStatus;
  defaultType?: SuggestionType;
  defaultQuery?: string;
  defaultArtists?: ArtistModel[];
  availableArtists?: ArtistModel[];
  onStatusChange: (status: ProcessStatus) => void;
  onTypeChange: (type: SuggestionType) => void;
  onQueryChange: (query: string) => void;
  onArtistChange: (artists: ArtistModel[]) => void;
}

export default function ListingDataSuggestionsFiltersForm({
  defaultStatus = ProcessStatus.PENDING,
  defaultType = SuggestionType.ALL,
  defaultQuery = '',
  defaultArtists = [],
  availableArtists = [],
  onStatusChange,
  onTypeChange,
  onQueryChange,
  onArtistChange
}: ListingDataSuggestionsFiltersFormProps) {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(defaultStatus);
  const [selectedType, setSelectedType] = useState<SuggestionType>(defaultType);
  const [query, setQuery] = useState<string>(defaultQuery);
  const [selectedArtists, setSelectedArtists] = useState<ArtistModel[]>(defaultArtists);

  // Sync internal state with prop changes
  useEffect(() => {
    setSelectedStatus(defaultStatus);
  }, [defaultStatus]);

  useEffect(() => {
    setSelectedType(defaultType);
  }, [defaultType]);

  useEffect(() => {
    setQuery(defaultQuery);
  }, [defaultQuery]);

  useEffect(() => {
    setSelectedArtists(defaultArtists);
  }, [defaultArtists]);

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
    onStatusChange(status);
  };

  const handleTypeChange = (type: SuggestionType) => {
    setSelectedType(type);
    onTypeChange(type);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    onQueryChange(value);
  };

  const handleArtistChange = (artists: ArtistModel[]) => {
    setSelectedArtists(artists);
    onArtistChange(artists);
  };

  return (
    <Stack spacing={4}>
      <HStack spacing={4} alignItems="flex-end">
        <FormControl flex={2}>
          <SearchBar
            onChange={handleQueryChange}
            defaultValue={defaultQuery}
          />
        </FormControl>
        <FormControl flex={1}>
          <FormLabel fontSize="sm" htmlFor="listing-data-suggestion-artist">Artist</FormLabel>
          <ArtistDropdown
            artists={availableArtists}
            onArtistSelect={handleArtistChange}
            defaultSelectedArtists={selectedArtists}
            multiSelectable={true}
            maxWidth={true}
          />
        </FormControl>
        <FormControl flex={1}>
          <FormLabel fontSize="sm" htmlFor="listing-data-suggestion-type">Type</FormLabel>
          <SuggestionTypeDropdown
            value={selectedType}
            onChange={handleTypeChange}
            size="md"
          />
        </FormControl>
        <FormControl flex={1}>
          <FormLabel fontSize="sm" htmlFor="listing-data-suggestion-status">Status</FormLabel>
          <StatusDropdown
            value={selectedStatus}
            onChange={handleStatusChange}
            size="md"
          />
        </FormControl>
      </HStack>
    </Stack>
  );
}
